import { identity, isFunction, isPlainObject, mapValues } from 'lodash'
import { useMemo, useRef } from 'react'
import jsonata from '~/common/jsonata'
import usePrevious from '~/hooks/usePrevious'

// Allowed token patterns: {{...}} or ${...} or {{{...}}}
// Triple brackets will never be auto cast, allowing you to control casting via jsonata
const stringTokenRegex = /(?:{{{([^}]*)}}}|{{([^}]*)}}|\${([^}]*)})/g

export const isManuallyCastToken = token => /^{{{([^}]*)}}}$/.test(token)

export function useTokenizedUrl(apiConfig, tokenMappers, mapperFnArgs) {
  const urlOrGraphql = getFullUrlFromApiConfig(apiConfig)
  return useTokenizedString(urlOrGraphql, tokenMappers, mapperFnArgs, match => encodeURIComponent(match ?? ''))
}

export function useTokenizedString(string, tokenMappers, mapperFnArgs, replaceMiddleware) {
  const tokenized = useMemo(
    () => resolveStringTokens(string, tokenMappers, mapperFnArgs, replaceMiddleware),
    [string, ...tokenMappers]
  )
  const prev = usePrevious(tokenized)
  const hasChanged = tokenized !== prev && prev !== undefined
  const hasChangedCount = useRef(0)
  if (hasChanged) hasChangedCount.current += 1
  return [tokenized, hasChanged, hasChangedCount.current]
}

export function resolveUrlTokens(apiConfig, tokenMappers, mapperFnArgs) {
  const urlOrGraphql = getFullUrlFromApiConfig(apiConfig)
  return resolveStringTokens(urlOrGraphql, tokenMappers, mapperFnArgs, match => encodeURIComponent(match ?? ''))
}

export const __SKIP_TOKEN_MAPPING__ = '__SKIP_TOKEN_MAPPING__'

export function resolveStringTokens(string, tokenMappers, mapperFnArgs = [], replaceMiddleware = identity) {
  if (!stringContainsTokens(string)) {
    return string
  }

  let tokens = {}
  tokenMappers.forEach(mapper => {
    if (isFunction(mapper)) {
      tokens = { ...tokens, ...mapper(...mapperFnArgs) }
    }
    if (isPlainObject(mapper)) {
      tokens = {
        ...tokens,
        ...mapValues(mapper, val => {
          if (val?.[__SKIP_TOKEN_MAPPING__]) return undefined
          if (isFunction(val)) return val(...mapperFnArgs)
          return val
        })
      }
    }
  })

  // If the string is a single token itself, then we will return the jsonata
  // result directly without forcing it to a string
  const isStringIsSingleToken = stringIsSingleToken(string)
  let resolvedSingleToken

  // Otherwise, it's a string that contains one or more tokens, so the return
  // will still be a string but with it's tokens resolved and concatenated into
  // the final returned string.

  // Have to reset the lastIndex to avoid this bug:
  // https://stackoverflow.com/questions/3827456/what-is-wrong-with-my-date-regex/3827500#3827500
  stringTokenRegex.lastIndex = 0

  // Replace expression tokens but it's still a string
  const stringWithResolvedTokens = string.replace(stringTokenRegex, (_, ...possibleMatches) => {
    // There are three allowable token syntaxes: {{token}} or ${token} or {{{token}}}
    // So we need to check if there was a match for any of them, only one will have a value.
    const match = possibleMatches.filter(Boolean)[0]
    const jsonatafied = jsonata(match).evaluate(tokens)
    const middlewarified = replaceMiddleware(jsonatafied ?? '')
    if (isStringIsSingleToken) resolvedSingleToken = middlewarified
    return middlewarified
  })

  // Return either the direct output of a single resolve jsonata expression OR
  // the string with resolved tokens
  return resolvedSingleToken ?? stringWithResolvedTokens
}

export function urlContainsTokens(apiConfig) {
  return stringContainsTokens(getFullUrlFromApiConfig(apiConfig))
}

export function stringContainsTokens(string) {
  if (typeof string !== 'string' || !string.includes('{')) return false
  // Have to reset the lastIndex to avoid this bug:
  // https://stackoverflow.com/questions/3827456/what-is-wrong-with-my-date-regex/3827500#3827500
  stringTokenRegex.lastIndex = 0
  return stringTokenRegex.test(string ?? '')
}

export function stringIsSingleToken(string) {
  return new RegExp(`^${stringTokenRegex.source}$`).test(string ?? '')
}

export function getFullUrlFromApiConfig(apiConfig) {
  if (!apiConfig) return

  const { url, queries, graphql } = apiConfig

  if (!url && !graphql)
    throw new Error('You must provide either a `url` or `graphql` param within the apiConfig object.')

  let urlOrGraphql

  if (url) {
    if (queries && url.includes('?')) {
      throw new Error('apiConfig url cannot contain query string if you are also using apiConfig.queries')
    }
    const queryString = Array.isArray(queries) ? `?${queries.join('&')}` : ''
    urlOrGraphql = url + queryString
  }

  if (graphql) {
    urlOrGraphql = graphql
  }

  // eslint-disable-next-line consistent-return
  return urlOrGraphql
}
